import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppButton, Box, Preloader } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT, RAW_DATE_FORMAT } from '@/common/constants';
import { WarnModal } from '@/components/elements';
import useValidatePayrollCalendarForm from '@/modules/employer/components/SetupEmployer/Features/PayrollCalendar/hooks/useValidatePayrollCalendarForm';
import {
  PayrollCalendarFormFields,
  PayrollCalendarFormValues,
  PayrollCalendarFrequency,
} from '@/modules/employer/components/SetupEmployer/Features/PayrollCalendar/payrollCalendar.types';
import { PayrollCalendarParamsForm } from '@/modules/employer/components/SetupEmployer/Features/PayrollCalendar/PayrollCalendarParams/PayrollCalendarParamsForm';
import {
  comparePayrollCalendarParams,
  paramsBuilder,
} from '@/modules/employer/components/SetupEmployer/Features/PayrollCalendar/PayrollCalendarParams/PayrollCalendarParamsModalForm';
import useCalculatePayrollCalendarScheduleQuery from '@/modules/employer/components/SetupEmployer/Features/PayrollCalendar/queries/useCalculatePayrollCalendarSchedule.query';

import { usePayrollCalendarParamsForm } from './hooks/usePayrollCalendarParamsForm';
import { PayrollCalendarModalSteps } from './stores/useRenewalPayrollCalendarModal.store';
import {
  useEditablePayrollCalendarStore,
  usePayrollCalendarDatesStore,
  usePayrollCalendarModalStore,
  usePayrollCalendarParamsStore,
} from './stores';

interface Props {
  onCancel: () => void;
}

// Renewal fork of PayrollCalendarParamsModalForm (step 1): params are hydrated from
// the editable store (no getById query), reusing the shared form, paramsBuilder and schedule query.
const RenewalPayrollCalendarParamsStep: React.FC<Props> = ({ onCancel }) => {
  const [isServerError, setIsServerError] = useState(false);
  const [backLoading, setBackLoading] = useState(false);
  const [formValues, setFormValues] = useState<PayrollCalendarFormValues>({});

  const editMode = usePayrollCalendarModalStore((state) => state.editMode);
  const handleSetStep = usePayrollCalendarModalStore((state) => state.handleSetStep);
  const handleSetDates = usePayrollCalendarDatesStore((state) => state.handleSetDates);
  const handleSetInitialMonthDate = usePayrollCalendarDatesStore((state) => state.handleSetInitialMonthDate);
  const handleSetMinDate = usePayrollCalendarDatesStore((state) => state.handleSetMinDate);
  const handleSetEndDate = usePayrollCalendarDatesStore((state) => state.handleSetEndDate);
  const handleSetCalendarParams = usePayrollCalendarParamsStore((state) => state.handleSetCalendarParams);
  const calendarParams = usePayrollCalendarParamsStore((state) => state.calendarParams);
  const handleReset = usePayrollCalendarParamsStore((state) => state.handleReset);
  const data = useEditablePayrollCalendarStore((state) => state.editableCalendar);

  const { isLoading, calculateSchedule } = useCalculatePayrollCalendarScheduleQuery();

  const fields = usePayrollCalendarParamsForm({
    formValues,
    defaultValues: data?.config,
    alreadyUsedValues: calendarParams,
  });

  const handleSubmit = useCallback(async () => {
    if (formValues?.[PayrollCalendarFormFields.FREQUENCY] === PayrollCalendarFrequency.ANNUALLY) {
      if (!formValues[PayrollCalendarFormFields.DATE_OF_YEAR]) {
        return;
      }
      const currentDate = dayjs(formValues[PayrollCalendarFormFields.DATE_OF_YEAR], DEFAULT_DATE_FORMAT);
      const startDate = dayjs(formValues[PayrollCalendarFormFields.START_DATE], DEFAULT_DATE_FORMAT);
      const isCurrentDateIsDefault =
        currentDate.format(DEFAULT_DATE_FORMAT) === data?.config[PayrollCalendarFormFields.DATE_OF_YEAR];
      const today = dayjs();
      if (!currentDate.isValid() || !startDate.isValid()) {
        return;
      }
      if (startDate.isAfter(currentDate) && !isCurrentDateIsDefault) {
        return;
      }
      if (!currentDate.isAfter(today) && !currentDate.isBefore(startDate) && !isCurrentDateIsDefault) {
        return;
      }
    }

    try {
      const currentParams = paramsBuilder(formValues);
      // When editing and the params were NOT changed, keep the saved schedule
      // (which may include the user's manual date deletions).
      const isUnchanged =
        editMode && !!data && comparePayrollCalendarParams(currentParams, paramsBuilder(data.config));
      const dates = isUnchanged ? data?.schedule : await calculateSchedule(currentParams);
      const sortedDates = (dates || []).sort((a, b) => dayjs(a).diff(dayjs(b)));

      handleSetDates(sortedDates);
      handleSetStep(PayrollCalendarModalSteps.SET_PAYROLL_CALENDAR_DATES);
      handleSetInitialMonthDate(
        dayjs(formValues[PayrollCalendarFormFields.START_DATE]).format(RAW_DATE_FORMAT)
      );

      if (editMode && data?.endDate) {
        handleSetEndDate(data.endDate);
      }

      if (dayjs(formValues[PayrollCalendarFormFields.START_DATE]).isAfter(dayjs())) {
        handleSetMinDate(formValues[PayrollCalendarFormFields.START_DATE]);
      } else {
        handleSetMinDate(dayjs().add(1, 'day').format(RAW_DATE_FORMAT));
      }
      handleSetCalendarParams(formValues);
    } catch {
      setIsServerError(true);
    }
  }, [
    formValues,
    calculateSchedule,
    handleSetDates,
    handleSetStep,
    handleSetInitialMonthDate,
    handleSetCalendarParams,
    data,
    editMode,
    handleSetMinDate,
    handleSetEndDate,
  ]);

  const { isSubmitted, handleValidate } = useValidatePayrollCalendarForm({
    fields,
    state: formValues,
    onSuccess: handleSubmit,
  });

  const handleCancel = useCallback(() => {
    onCancel();
    handleReset();
  }, [onCancel, handleReset]);

  const isSetFormValuesCalledRef = useRef(false);
  const isBackFromNextStep = useRef(false);

  useEffect(() => {
    if (editMode && data && fields.length > 3 && !isSetFormValuesCalledRef.current) {
      setFormValues(data.config);
      isSetFormValuesCalledRef.current = true;
    }
  }, [editMode, data, fields.length]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (calendarParams && !isBackFromNextStep.current) {
      setBackLoading(true);
      timeout = setTimeout(() => {
        setBackLoading(false);
        isBackFromNextStep.current = true;
      }, 200);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [calendarParams]);

  return (
    <>
      <Box data-testid="RenewalPayrollCalendarParamsStep_container">
        <Box direction="column">
          <Box round="container1Round" pad="spacing24" background="module">
            {backLoading ? (
              <Preloader />
            ) : (
              <PayrollCalendarParamsForm
                handleChangeValues={setFormValues}
                isSubmit={isSubmitted}
                fields={fields}
              />
            )}
          </Box>
        </Box>
        <Box
          direction="row"
          fill="horizontal"
          margin={{ top: 'spacing24' }}
          align="end"
          justify="end"
          width="medium"
          gap="xs"
        >
          <Box direction="row" gap="xs">
            <AppButton
              testId="RenewalPayrollCalendarParamsStep_cancel"
              buttonType="secondary"
              width="control"
              type="button"
              onClick={handleCancel}
            >
              Cancel
            </AppButton>
            <AppButton
              testId="RenewalPayrollCalendarParamsStep_submit"
              type="button"
              disabled={isLoading}
              onClick={handleValidate}
              width="control"
            >
              {isLoading ? <Preloader /> : 'Next (1/2)'}
            </AppButton>
          </Box>
        </Box>
      </Box>

      <WarnModal
        testId="renewal-payroll-calendar-params-server-error-modal"
        visible={isServerError}
        onSetVisible={setIsServerError}
        header="The server encountered an error processing the request"
        helptext={`It doesn't appear to have affected your data, but we cannot proceed with the latest records.
          Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`}
        buttonText="Close"
        onSubmit={handleSubmit}
        submitButtonText="Try again"
      />
    </>
  );
};

export default RenewalPayrollCalendarParamsStep;
